import { useCallback, useEffect, useState } from 'react';

import { resolveMessageSources } from './replyPageSources';
import type { AiAssistantMessage, AiAssistantSourcesData } from './types';

type UseActiveSourcesOptions = {
  activeChatId: string | null;
  open: boolean;
  view: 'chat' | 'list';
};

/**
 * Manages the pull-out sources panel and its link to chat messages.
 *
 * - {@link activeSources} holds the message whose sources are shown (id, text,
 *   and source list), or `null` when the panel is closed.
 * - {@link sourcesTarget} is the citation index to scroll to and flash when
 *   {@link jumpToSource} is used (portal-starter: chip click → sources panel).
 *   The live assistant currently opens the previewer from the chip instead;
 *   keep this hook so that path can be restored.
 *
 * {@link openSourcesForMessage} toggles sources for a message (open/close).
 * {@link closeSources} closes the panel. Both reset when the assistant closes,
 * the user leaves chat view, or {@link activeChatId} changes.
 */
export function useActiveSources({
  activeChatId,
  open,
  view,
}: UseActiveSourcesOptions) {
  const [activeSources, setActiveSources] =
    useState<AiAssistantSourcesData | null>(null);
  const [sourcesTarget, setSourcesTarget] = useState<number | null>(null);

  const closeSources = useCallback(() => {
    setActiveSources(null);
    setSourcesTarget(null);
  }, []);

  useEffect(() => {
    if (!open || view !== 'chat') {
      closeSources();
    }
  }, [closeSources, open, view]);

  useEffect(() => {
    closeSources();
  }, [activeChatId, closeSources]);

  const openSourcesForMessage = useCallback(
    (message: AiAssistantMessage) => {
      if (activeSources?.messageId === message.id) {
        closeSources();
        return;
      }
      setActiveSources({
        messageId: message.id,
        sources: resolveMessageSources(message),
      });
      setSourcesTarget(null);
    },
    [activeSources?.messageId, closeSources]
  );

  const jumpToSource = useCallback(
    (message: AiAssistantMessage, number: number) => {
      setActiveSources({
        messageId: message.id,
        sources: resolveMessageSources(message),
      });
      // Re-set to a fresh value each click so the panel re-centers even when the
      // same source number is clicked twice.
      setSourcesTarget(null);
      requestAnimationFrame(() => setSourcesTarget(number));
    },
    []
  );

  return {
    activeSources,
    closeSources,
    jumpToSource,
    openSourcesForMessage,
    sourcesTarget,
  };
}
